import {toast} from "react-toastify";
import {useDeleteWorksheetLineNoteMutation, useLoadSingleWorksheetLineNoteLazyQuery, useUpsertWorksheetLineNoteMutation} from "../../../__generated__/generated_types";
import {useEffect, useState} from "react";
import {DriverNote} from "../DriverNoteModal";


export interface UseDriverNoteModal {
    driverNoteData: DriverNote | null;
    isBusy: boolean;
    fetchNote: (lineId: string) => void;
    save: (driverNote: DriverNote) => void;
    reset: () => void;
}

interface WsDriverNoteModalProps {
    onSave?: () => void;
}

export default function useWsDriverNoteModal(props?: WsDriverNoteModalProps): UseDriverNoteModal {
    const [driverNoteData, setDriverNoteData] = useState<DriverNote | null>(null);

    const [fetchNoteLocal, {data: noteData, loading: noteLoading}] = useLoadSingleWorksheetLineNoteLazyQuery({fetchPolicy: "no-cache"});

    const [deleteNote, {loading: noteDeleting}] = useDeleteWorksheetLineNoteMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.deleteWorksheetLineNote) {
                toast.success("Note deleted");
                props?.onSave?.();
            }
        },
    });

    const [upsertNote, {loading: noteSaving}] = useUpsertWorksheetLineNoteMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.upsertWorksheetLineNote) {
                toast.success("Note updated");
                props?.onSave?.();
            }
        },
    });

    useEffect(() => {
        const note = noteData?.loadSingleWorksheetLineNote;
        if (note) {
            setDriverNoteData(prev => {
                if (!prev) {
                    return null;
                }
                return {
                    lineType: "worksheet",
                    lineId: prev.lineId,
                    lineName: note.lineName,
                    lineValuesLocked: note.lineValuesLocked,
                    note: note.note,
                    isReadOnly: note.isReadOnly,
                };
            });
        }
    }, [noteData]);

    // user experience should be the same whether they are creating or updating a note, so a
    // partial driverNote object is still valid to launch the modal. If the note exists, then as the fetch
    // gets data back it will populate the rest of the object.
    function fetchNote(lineId: string): void {
        setDriverNoteData({
            lineType: "worksheet",
            lineId,
            lineName: "",
            lineValuesLocked: false,
            note: null,
            isReadOnly: null,
        });

        fetchNoteLocal({
            variables: {
                worksheetLineId: lineId,
            },
        });
    }

    // I figured if the note theyre trying to save is empty, we could just go ahead and delete
    // the note record out of the DB, but if that seems like too heavy of an assumption we can make it an explicit interaction
    function save(driverNote: DriverNote): void {
        const trimmed = driverNote.note?.trim() ?? "";

        if (trimmed === "") {
            deleteNote({
                variables: {
                    worksheetLineId: driverNote.lineId,
                },
            });
        } else {
            upsertNote({
                variables: {
                    note: {
                        lineId: driverNote.lineId,
                        note: driverNote.note ?? "",
                        isReadOnly: driverNote.isReadOnly ?? false,
                    },
                },
            });
        }
    }

    function reset() {
        setDriverNoteData(null);
    }

    return {
        driverNoteData: driverNoteData,
        isBusy: noteLoading || noteSaving || noteDeleting,
        fetchNote: fetchNote,
        save: save,
        reset: reset,
    };
}
